/**
 * propose_navigation tool — Task C5
 *
 * Proposes navigating the user to another analytics page that better answers
 * their question. This tool does NOT navigate — it emits a nav proposal that
 * the user explicitly clicks to follow. Navigation asks for consent; the UI
 * hook performs the actual router push on click.
 *
 * Design decisions:
 * - No ctx/ApiClient needed: page routes come from the static PAGE_CATALOG.
 * - Uses the same Record<string, PageInfo | undefined> cast pattern as C2–C4
 *   so dynamic pageId lookup works without index-type errors.
 * - presetView is forwarded verbatim into the ui if provided; omitted otherwise.
 * - All errors surface as isError:true; nothing escapes run().
 */

import { PAGE_CATALOG } from '../grounding/pageCatalog'
import type { PageInfo } from '../grounding/pageCatalog'
import type { AdvisorTool, ToolResult } from '../runtime/types'

// Dynamic access helper — PAGE_CATALOG uses `satisfies` which retains literal
// keys, but callers receiving pageId at runtime must widen to avoid TS errors.
const CATALOG = PAGE_CATALOG as Record<string, PageInfo | undefined>

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function makeProposeNavigationTool(): AdvisorTool {
  return {
    name: 'propose_navigation',

    description:
      'Proposes navigating the user to another analytics page that better answers their question. ' +
      'Does NOT navigate automatically — emits a "Go to <page>" proposal that the user clicks to ' +
      'follow. Optionally pre-loads a specific view via presetView search-params. Use this when ' +
      "the user's question is better answered on a different page than the one they are viewing.",

    input_schema: {
      type: 'object',
      properties: {
        pageId: {
          type: 'string',
          description:
            'Identifier of the target analytics page. One of: ReviewKpis, MarketingInvests, ' +
            'CampaignPerformance, Contribution, Dynamics, MultiDeepDive.',
        },
        reason: {
          type: 'string',
          description:
            'Short human-readable explanation of why this page better answers the question. ' +
            'Shown in the proposal card in the advisor panel.',
        },
        presetView: {
          type: 'object',
          description:
            'Optional map of search-param keys to values appended to the navigation URL to ' +
            'pre-load a specific view. Use REAL param keys: the KPI is set via the cascade ' +
            'params kpi_root / kpi_driver / kpi_value_type (there is no "kpi" param); the ' +
            'Contribution decomposition lens is `view` with value "channels" or "groups" only. ' +
            'Timeframe uses tf_preset (or from + to), filters use dim.<Name>, group-by uses ' +
            'groupby. E.g. { kpi_root: "sales_roi", tf_preset: "last_12_weeks" } or ' +
            '{ view: "groups" }. When you pass from/to, tf_preset=custom is added automatically so the dates take effect.',
          additionalProperties: { type: 'string' },
        },
      },
      required: ['pageId', 'reason'],
    },

    async run(input: unknown): Promise<ToolResult> {
      try {
        // ------------------------------------------------------------------
        // 1. Narrow input
        // ------------------------------------------------------------------
        if (typeof input !== 'object' || input === null) {
          return {
            content: 'propose_navigation: input must be a JSON object',
            isError: true,
          }
        }
        const inp = input as Record<string, unknown>

        const pageId = typeof inp['pageId'] === 'string' ? inp['pageId'] : undefined
        if (!pageId) {
          return {
            content: 'propose_navigation: "pageId" is required and must be a string',
            isError: true,
          }
        }

        const reason = typeof inp['reason'] === 'string' ? inp['reason'] : undefined
        if (!reason) {
          return {
            content: 'propose_navigation: "reason" is required and must be a string',
            isError: true,
          }
        }

        // presetView is optional; accept only plain objects (not arrays/null)
        const presetViewRaw = inp['presetView']
        const presetView: Record<string, string> | undefined =
          presetViewRaw !== undefined &&
          typeof presetViewRaw === 'object' &&
          presetViewRaw !== null &&
          !Array.isArray(presetViewRaw)
            ? (presetViewRaw as Record<string, string>)
            : undefined

        // Raw from/to in a URL do NOT override a page's default timeframe preset —
        // tf_preset=custom must ride along (see reference_url_date_param_override).
        // The model routinely passes bare dates; normalise here rather than trusting
        // prompt guidance alone. An explicit tf_preset always wins.
        const normalizedPresetView: Record<string, string> | undefined =
          presetView !== undefined &&
          (presetView['from'] !== undefined || presetView['to'] !== undefined) &&
          presetView['tf_preset'] === undefined
            ? { tf_preset: 'custom', ...presetView }
            : presetView

        // ------------------------------------------------------------------
        // 2. Resolve page info via PAGE_CATALOG
        // ------------------------------------------------------------------
        const page = CATALOG[pageId]
        if (!page) {
          return {
            content: `propose_navigation: Unknown page: ${pageId}`,
            isError: true,
          }
        }

        // ------------------------------------------------------------------
        // 3. Return nav proposal (does NOT navigate)
        // ------------------------------------------------------------------
        return {
          content: 'proposal shown',
          ui: {
            kind: 'nav',
            pageId,
            route: page.route,
            reason,
            ...(normalizedPresetView !== undefined ? { presetView: normalizedPresetView } : {}),
          },
        }
      } catch (err) {
        // ------------------------------------------------------------------
        // 4. Nothing escapes run()
        // ------------------------------------------------------------------
        return {
          content: err instanceof Error ? err.message : String(err),
          isError: true,
        }
      }
    },
  }
}
